/**
 * Budget JSON Field Configurations
 *
 * This file defines all fields that should be stored in the budget_json column
 * for each platform and budget type. It serves as the single source of truth
 * for dynamic field management.
 *
 * USAGE:
 * - Add new fields here with their configuration
 * - The budgetJsonUtils.js will handle serialization/deserialization automatically
 * - No need to manually update GroupEditModal mapping logic
 */

/**
 * Field Configuration Schema:
 *
 * {
 *   name: string,              // Form field name (e.g., 'googleLanguages')
 *   budgetJsonPath: string,    // Path in budget_json (e.g., 'languages' or 'geo.countries')
 *   type: string,              // Field type: 'text', 'select', 'multiselect', 'textarea', 'number'
 *   label: string,             // Display label
 *   tooltip: string,           // Optional tooltip text
 *   required: boolean,         // Whether field is required
 *   format: string,            // How to transform value:
 *                              //   - 'csv_array': "a,b,c" -> ["a", "b", "c"]
 *                              //   - 'select_value': {label, value} -> value
 *                              //   - 'select_to_array': {label, value} -> [value]
 *                              //   - 'multiselect_values': [{label, value}, ...] -> [value, ...]
 *                              //   - 'number': "123" -> 123
 *   defaultValue: any,         // Default value for new budgets
 *   showInSetupFlow: array,    // ['quick', 'advanced'] or null for all
 *   showForBudgetTypes: array, // ['google_search', ...] or null for all
 * }
 */

export const BUDGET_JSON_FIELD_CONFIGS = {
  // ============================================================================
  // GOOGLE PLATFORM
  // ============================================================================
  google: {
    fields: [
      // Brand Name - Optional field for all Google ad types
      {
        name: 'googleBrandName',
        budgetJsonPath: 'brandName',
        type: 'text',
        label: 'Brand Name (Optional)',
        tooltip: 'Enter the brand name associated with this budget',
        required: false,
        format: null, // Plain text, no transformation
        showInSetupFlow: null, // Show in all flows
        showForBudgetTypes: null, // Show for all Google budget types
      },

      // Sales Country - Required for all Google ad types
      {
        name: 'googleSalesCountry',
        budgetJsonPath: 'salesCountry',
        type: 'select',
        label: 'Sales Country',
        tooltip: 'Select the primary country where sales will be attributed',
        required: true,
        format: 'select_value', // {label, value} -> value
        defaultValue: 'US',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Language Constants - Required for all Google ad types
      {
        name: 'googleLanguages',
        budgetJsonPath: 'languages',
        type: 'textarea',
        label: 'Language Constants (Required)',
        tooltip: 'Enter comma-separated Google language constants',
        required: true,
        format: 'csv_array', // "lang1, lang2" -> ["lang1", "lang2"]
        defaultValue: ['languageConstants/1000'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: States
      {
        name: 'geoStates',
        budgetJsonPath: 'geo.states',
        type: 'textarea',
        label: 'Target States',
        tooltip: 'Enter comma-separated state codes or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Cities
      {
        name: 'geoCities',
        budgetJsonPath: 'geo.cities',
        type: 'textarea',
        label: 'Target Cities',
        tooltip: 'Enter comma-separated city names or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Postal Codes
      {
        name: 'geoPostalCodes',
        budgetJsonPath: 'geo.postalCodes',
        type: 'textarea',
        label: 'Target Postal Codes',
        tooltip: 'Enter comma-separated postal codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },
    ],
  },

  // ============================================================================
  // META PLATFORM
  // ============================================================================
  meta: {
    fields: [
      // Languages - Multi-select for Meta
      {
        name: 'metaLanguages',
        budgetJsonPath: 'languages',
        type: 'multiselect',
        label: 'Languages',
        tooltip: 'Select the languages to target for your Meta ads',
        required: false,
        format: 'multiselect_values', // [{label, value}, ...] -> [value, ...]
        defaultValue: ['en_US'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries (Meta only supports countries)
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Gender Targeting
      {
        name: 'metaGender',
        budgetJsonPath: 'meta.targeting.genders',
        type: 'select',
        label: 'Gender Targeting',
        tooltip: 'Select the gender targeting for your Meta ads',
        required: false,
        format: 'select_to_array', // {label, value} -> [value]
        defaultValue: [0], // All genders
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Age Min
      {
        name: 'metaAgeMin',
        budgetJsonPath: 'meta.targeting.age_min',
        type: 'number',
        label: 'Min Age',
        tooltip: 'Minimum age for audience targeting (13-65)',
        required: false,
        format: 'number', // "25" -> 25
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Age Max
      {
        name: 'metaAgeMax',
        budgetJsonPath: 'meta.targeting.age_max',
        type: 'number',
        label: 'Max Age',
        tooltip: 'Maximum age for audience targeting (13-65)',
        required: false,
        format: 'number',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Custom Audiences - dynamic list of custom audience IDs to include
      {
        name: 'customAudienceIds',
        budgetJsonPath: 'meta.targeting.custom_audience_ids',
        type: 'audience_list',
        label: 'Custom Audiences',
        tooltip: 'Add custom audience IDs to include in targeting',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Excluded Custom Audiences - dynamic list of custom audience IDs to exclude
      {
        name: 'excludedCustomAudienceIds',
        budgetJsonPath: 'meta.targeting.excluded_custom_audience_ids',
        type: 'audience_list',
        label: 'Excluded Custom Audiences',
        tooltip: 'Add custom audience IDs to exclude from targeting',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Product Set ID - plain text, stored at meta.product_set_id (sibling of targeting)
      {
        name: 'productSetId',
        budgetJsonPath: 'meta.product_set_id',
        type: 'text',
        label: 'Product Set ID',
        tooltip: 'Meta product set ID for this budget',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries also mirrored under meta.targeting.geo_locations.countries
      // This reuses the same 'geoCountries' form field as the top-level geo.countries config
      // above, so a single input drives both locations in budget_json.
      {
        name: 'geoCountries',
        budgetJsonPath: 'meta.targeting.geo_locations.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Income Targeting - dynamic list of Meta income targeting IDs
      {
        name: 'metaIncome',
        budgetJsonPath: 'meta.targeting.flexible_spec[0].income',
        type: 'audience_list',
        label: 'Income Targeting',
        tooltip: 'Add Meta income targeting IDs to include in flexible targeting',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Family Status Targeting - dynamic list of Meta family status targeting IDs
      {
        name: 'metaFamilyStatuses',
        budgetJsonPath: 'meta.targeting.flexible_spec[0].family_statuses',
        type: 'audience_list',
        label: 'Family Status Targeting',
        tooltip: 'Add Meta family status targeting IDs to include in flexible targeting',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },
    ],
  },



  // ============================================================================
  // TIKTOK PLATFORM
  // ============================================================================
  tiktok: {
    fields: [
      // Languages - Multi-select for TikTok
      {
        name: 'tiktokLanguages',
        budgetJsonPath: 'languages',
        type: 'multiselect',
        label: 'Languages',
        tooltip: 'Select the languages to target for your TikTok ads',
        required: false,
        format: 'multiselect_values',
        defaultValue: ['en_US'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },
    ],
  },

  // ============================================================================
  // AMAZON PLATFORM - DSP-SPECIFIC FIELDS
  // ============================================================================
  amazon: {
    fields: [
      // ----------------------------------------------------------------------
      // KEYWORDS (root-level, platform-agnostic - will expand to other platforms)
      // Stored at the root of budget_json (NOT under an ad-platform node) so the
      // same structure can be reused for other platforms later.
      // ----------------------------------------------------------------------

      // Negative Keywords - comma/newline separated list stored as an array
      {
        name: 'negativeKeywords',
        budgetJsonPath: 'negativeKeywords.keywords',
        type: 'textarea',
        label: 'Negative Keywords',
        tooltip: 'Enter comma-separated negative keywords (line breaks are converted to commas)',
        required: false,
        format: 'csv_array',
        showInSetupFlow: null,
        showForBudgetTypes: null, // Show for all Amazon budget types
      },

      // Negative Keywords - Negative Phrase match toggle
      {
        name: 'negativePhrase',
        budgetJsonPath: 'negativeKeywords.negativePhrase',
        type: 'checkbox',
        label: 'Negative Phrase',
        tooltip: 'Apply the negative keywords as Negative Phrase match',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Negative Keywords - Negative Exact match toggle
      {
        name: 'negativeExact',
        budgetJsonPath: 'negativeKeywords.negativeExact',
        type: 'checkbox',
        label: 'Negative Exact',
        tooltip: 'Apply the negative keywords as Negative Exact match',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Custom Keywords - comma/newline separated list stored as an array
      {
        name: 'customKeywords',
        budgetJsonPath: 'customKeywords.keywords',
        type: 'textarea',
        label: 'Custom Keywords',
        tooltip: 'Enter comma-separated custom keywords (line breaks are converted to commas)',
        required: false,
        format: 'csv_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Custom Keywords - Broad match toggle
      {
        name: 'customBroad',
        budgetJsonPath: 'customKeywords.broad',
        type: 'checkbox',
        label: 'Broad',
        tooltip: 'Apply the custom keywords as Broad match',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Custom Keywords - Phrase match toggle
      {
        name: 'customPhrase',
        budgetJsonPath: 'customKeywords.phrase',
        type: 'checkbox',
        label: 'Phrase',
        tooltip: 'Apply the custom keywords as Phrase match',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Custom Keywords - Exact match toggle
      {
        name: 'customExact',
        budgetJsonPath: 'customKeywords.exact',
        type: 'checkbox',
        label: 'Exact',
        tooltip: 'Apply the custom keywords as Exact match',
        required: false,
        format: null,
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // DSP Gender (stored in budget_json.dsp.gender)
      {
        name: 'dspGender',
        budgetJsonPath: 'dsp.gender',
        type: 'select',
        label: 'Gender Targeting',
        tooltip: 'Select gender targeting for DSP ads',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'], // Only for DSP budget type
      },

      // DSP Ad Format
      {
        name: 'dspAdFormat',
        budgetJsonPath: 'dsp.ad_format',
        type: 'select',
        label: 'Ad Format',
        tooltip: 'Select the ad format for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },

      // DSP Spend Tactic
      {
        name: 'dspSpendTactic',
        budgetJsonPath: 'dsp.spend_tactic',
        type: 'select',
        label: 'Spend Tactic',
        tooltip: 'Select the spend tactic for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },

      // DSP Spend Speed
      {
        name: 'dspSpendSpeed',
        budgetJsonPath: 'dsp.spend_speed',
        type: 'select',
        label: 'Spend Speed',
        tooltip: 'Select the spend speed for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },
    ],
  },

  // ============================================================================
  // WALMART PLATFORM
  // ============================================================================
  walmart: {
    fields: [
      // Currently no walmart-specific budget_json fields
      // Add fields here as needed
    ],
  },

  // ============================================================================
  // TARGET PLATFORM
  // ============================================================================
  target: {
    fields: [
      // Promoted Brand IDs - dynamic list, stored at target.promotedBrandIds
      {
        name: 'targetPromotedBrandIds',
        budgetJsonPath: 'target.promotedBrandIds',
        type: 'audience_list',
        label: 'Promoted Brand IDs',
        tooltip: 'Add the Target promoted brand IDs for this budget',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Drawable Balance IDs - dynamic list, stored at target.drawableBalanceIds
      {
        name: 'targetDrawableBalanceIds',
        budgetJsonPath: 'target.drawableBalanceIds',
        type: 'audience_list',
        label: 'Drawable Balance IDs',
        tooltip: 'Add the Target drawable balance IDs for this budget',
        required: false,
        format: 'string_array',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },
    ],
  },
};

/**
 * Get field configuration for a specific platform and budget type
 * @param {string} platform - Platform key (e.g., 'google', 'meta')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp')
 * @param {string} setupFlow - Setup flow ('quick' or 'advanced')
 * @returns {array} Array of field configurations that should be displayed
 */
export function getFieldsForContext(platform, budgetType, setupFlow) {
  const platformConfig = BUDGET_JSON_FIELD_CONFIGS[platform];
  if (!platformConfig || !platformConfig.fields) {
    return [];
  }

  return platformConfig.fields.filter(field => {
    // Check setupFlow filter
    if (field.showInSetupFlow && !field.showInSetupFlow.includes(setupFlow)) {
      return false;
    }

    // Check budgetType filter
    if (field.showForBudgetTypes && !field.showForBudgetTypes.includes(budgetType)) {
      return false;
    }

    return true;
  });
}

/**
 * Get all fields for a platform (regardless of context)
 * @param {string} platform - Platform key
 * @returns {array} Array of all field configurations for the platform
 */
export function getAllFieldsForPlatform(platform) {
  const platformConfig = BUDGET_JSON_FIELD_CONFIGS[platform];
  if (!platformConfig || !platformConfig.fields) {
    return [];
  }
  return platformConfig.fields;
}
